import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ExpandMore from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '40px',
    backgroundColor: theme.palette.white,
    minWidth: props => (props.minWidth ? props.minWidth : '250px'),
    paddingRight: '52px !important',
    color: theme.palette.grey.dark,
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      color: theme.palette.primary.main
    },
    '& .MuiAutocomplete-input': {
      padding: '2px 4px !important'
    }
  },
  input: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: `1px solid ${theme.palette.grey.medium}`,
        borderRadius: '4px',
        paddingLeft: '16px',
        boxSizing: 'border-box',
        height: '45px',
        display: 'flex',
        alignItems: 'center'
      },
      '&:hover fieldset': {
        borderColor: theme.palette.accent.main
      },
      '&.Mui-focused fieldset': {
        borderRadius: '4px'
      }
    }
  },
  icon: {
    color: 'inherit'
  },
  titleOption : {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  optionsList: {
    fontSize: '.925rem',
    color: theme.palette.grey.dark,
    '&:first-child': {
      height: '38px'
    },
    '&:focus': {
      backgroundColor: 'inherit'
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.accent.main
    },
    '&[aria-disabled="true"]': {
      opacity: 0.6,
      color: 'black'
    },
  },
  label: {
    fontSize: '.85rem'
  },
  select: {
    paddingRight: '32px !important'
  },
  listbox: {
    maxHeight: props => (props.listBoxHeight ? props.listBoxHeight : '60vh')
  }
}));
const DropdownAutocomplete = ({
  options,
  selected,
  minWidth,
  type,
  onSelect,
  listBoxHeight,
  onRemove,
  name,
  label,
  disableClearable,
  ...rest
}) => {
  const classes = useStyles({ minWidth, listBoxHeight });

  const selectedValue = options.find(item => item.value === selected);

  const ref = useRef(null);

  const handleValueChange = (event, newValue, reason) => {
    if (!newValue && onRemove) {
      onRemove(ref.current.getAttribute('name'));
      return;
    }

    if (disableClearable && reason === 'clear') {
      // Clear button is disabled, do nothing
      return;
    }
    
    if (reason === 'clear') {
      onSelect({ value: null, type, label: '' });
      return;
    }

    if (newValue && newValue.label && newValue.value) {
      const { value, label } = newValue;
      onSelect({ value, type, label });
    }
  };

  return (
    <Autocomplete
      name={name}
      ref={ref}
      value={selectedValue || null}
      onChange={handleValueChange}
      options={options}
      getOptionLabel={option => option.label}
      getOptionDisabled={option => option.value === 0}
      fullWidth
      classes={{
        inputRoot: classes.root,
        popupIndicator: classes.icon,
        option: classes.optionsList,
        listbox: classes.listbox,
        clearIndicator: classes.icon
      }}
      blurOnSelect
      popupIcon={<ExpandMore />}
      renderOption={(option) => (
        <span className={option.label.includes('--') ? classes.titleOption : null}>{option.label}</span>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={label}
          classes={{
            root: classes.input
          }}
          variant="outlined"
        />
      )}
      {...rest}
    />
  );
};
DropdownAutocomplete.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  listBoxHeight: PropTypes.string,
  onRemove: PropTypes.func,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string
};

DropdownAutocomplete.defaultProps = {
  options: null,
  minWidth: null,
  listBoxHeight: null,
  selected: null,
  onRemove: null,
  name: null,
  label: null
};

export default DropdownAutocomplete;
